import { useState, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { ChatMessage, ElementComment, InteractionState } from '../types';

/**
 * Custom hook for managing template state
 */
export const useTemplateState = () => {
  // Core template state
  const [generatedCode, setGeneratedCode] = useState<string | null>(null);
  const [generatedSchema, setGeneratedSchema] = useState<string | null>(null);
  const [inputJson, setInputJson] = useState<string>('');
  const [isViewingTemplate, setIsViewingTemplate] = useState(false);
  
  // Action context
  const [currentActionName, setCurrentActionName] = useState<string | null>(null);
  const [currentActionId, setCurrentActionId] = useState<string | null>(null);
  const [currentActionData, setCurrentActionData] = useState<any>(null);
  const [currentUsageId, setCurrentUsageId] = useState<string | null>(null);
  
  // Chat and interaction
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [interactionState, setInteractionState] = useState<InteractionState>({ isActive: false });
  const [instructionText, setInstructionText] = useState('');
  
  // Edit mode comments
  const [elementComments, setElementComments] = useState<ElementComment[]>([]);
  
  // Speech mode
  const [isSpeechMode, setIsSpeechMode] = useState(false);
  
  // Template data for edit mode
  const [editTemplateData, setEditTemplateData] = useState<any>(null);

  // Helper functions
  const addMessage = useCallback((role: 'user' | 'assistant', content: React.ReactNode) => {
    const newMessage: ChatMessage = {
      id: uuidv4(),
      role,
      content,
      timestamp: new Date().toISOString(),
    };
    setMessages(prev => [...prev, newMessage]);
    return newMessage.id;
  }, []);

  const updateMessage = useCallback((id: string, content: React.ReactNode) => {
    setMessages(prev => prev.map(msg => msg.id === id ? { ...msg, content } : msg));
  }, []);

  const addElementComment = useCallback((elementText: string, comment: string) => {
    const newComment: ElementComment = {
      id: uuidv4(),
      elementText,
      comment: comment.trim(),
      timestamp: new Date().toISOString(),
    };
    setElementComments(prev => [...prev, newComment]);
  }, []);

  const removeElementComment = useCallback((commentId: string) => {
    setElementComments(prev => prev.filter(c => c.id !== commentId));
  }, []);

  const clearElementComments = useCallback(() => {
    setElementComments([]);
  }, []);

  const resetInteraction = useCallback(() => {
    setInteractionState({ isActive: false });
    setInstructionText('');
  }, []);

  return {
    // State
    generatedCode,
    generatedSchema,
    inputJson,
    isViewingTemplate,
    currentActionName,
    currentActionId,
    currentActionData,
    currentUsageId,
    messages,
    interactionState,
    instructionText,
    elementComments,
    isSpeechMode,
    editTemplateData,
    
    // Setters
    setGeneratedCode,
    setGeneratedSchema,
    setInputJson,
    setIsViewingTemplate,
    setCurrentActionName,
    setCurrentActionId,
    setCurrentActionData,
    setCurrentUsageId,
    setMessages,
    setInteractionState,
    setInstructionText,
    setElementComments,
    setIsSpeechMode,
    setEditTemplateData,
    
    // Helper functions
    addMessage,
    updateMessage,
    addElementComment,
    removeElementComment,
    clearElementComments,
    resetInteraction,
  };
};
